import { MarkdownProse } from "@/components/ui/markdown-prose";
import { Dateline } from "@/components/ui/dateline";
import { Kicker } from "@/components/ui/kicker";
import { PersonaAvatar } from "@/components/persona-identity";
import { PersonaVoice } from "@/components/ui/persona-voice";
import { VoiceProse } from "@/components/ui/voice-prose";
import { SeverityDot, severityTone } from "@/components/ui/severity-dot";
import { FindingPolarityMark } from "@/components/experience/finding-polarity-mark";
import { HumanTestVerdicts } from "@/components/walkthrough/human-test-verdicts";
import {
  StepScreenshot,
  isValidBox,
  type ScreenshotHighlight,
  type TargetBox,
} from "@/components/walkthrough/step-screenshot";
import { useStepScreenshot } from "@/hooks/use-step-screenshot";
import type { FindingType, Severity } from "@/components/finding-badges";
import { isProblemType } from "@iamu/shared";
import { cn } from "@/lib/utils";

export interface PrintStep {
  id: string;
  stepNumber: number;
  action: string;
  screenTitle?: string | null;
  personaReasoning?: string | null;
  url?: string | null;
  targetBox?: TargetBox | null;
}

export interface PrintFinding {
  id: string;
  title: string;
  type: FindingType;
  severity?: Severity | null;
  uiFact?: string | null;
  consequence?: string | null;
  recommendation?: string | null;
  walkthroughStepId?: string | null;
  stepNumber?: number | null;
  targetBox?: TargetBox | null;
}

export interface WalkthroughPrintDocumentProps {
  scenarioName: string;
  scenarioNarrative?: string | null;
  persona?: {
    name: string;
    role?: string | null;
    context?: string | null;
  } | null;
  walkthroughId: string;
  runNumber?: number | string | null;
  startedAt?: string | null | Date;
  completedAt?: string | null | Date;
  targetUrl?: string | null;
  steps: PrintStep[];
  findings: PrintFinding[];
  verdicts?: Array<{ question: string; rating: string; reasoning?: string }>;
  whatWorked?: string[];
  openQuestions?: string[];
  className?: string;
}

function formatDuration(
  startedAt: string | null | Date | undefined,
  completedAt: string | null | Date | undefined,
) {
  if (!startedAt || !completedAt) return null;
  const ms = new Date(completedAt).getTime() - new Date(startedAt).getTime();
  if (ms < 0) return null;
  const mins = Math.floor(ms / 60000);
  if (mins < 1) return "<1 min";
  if (mins < 60) return `${mins} min`;
  const hours = Math.floor(mins / 60);
  const rem = mins % 60;
  return rem === 0 ? `${hours} hr` : `${hours} hr ${rem} min`;
}

function formatDate(value: string | null | Date | undefined): string | null {
  if (!value) return null;
  return new Date(value).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function PrintStepItem({
  step,
  walkthroughId,
  highlights,
}: {
  step: PrintStep;
  walkthroughId: string;
  highlights?: ScreenshotHighlight[];
}) {
  const { url } = useStepScreenshot(walkthroughId, step.stepNumber);

  return (
    <div className="space-y-3 rounded-lg border border-border bg-card p-4 break-inside-avoid">
      <div className="flex items-center justify-between border-b border-border/60 pb-2">
        <div className="flex items-center gap-2">
          <span className="flex h-5 w-5 items-center justify-center rounded bg-muted text-xs font-semibold text-muted-foreground tabular-nums">
            {step.stepNumber}
          </span>
          <span className="text-sm font-semibold text-foreground">
            {step.action}
          </span>
        </div>
        {step.url && (
          <span className="text-xs text-muted-foreground truncate max-w-[280px]">
            {step.url}
          </span>
        )}
      </div>

      {step.personaReasoning && (
        <div className="rounded border border-border/60 bg-muted/20 px-3 py-2">
          <PersonaVoice className="text-sm">
            {step.personaReasoning}
          </PersonaVoice>
        </div>
      )}

      {url && (
        <div className="overflow-hidden rounded border border-border/80">
          <StepScreenshot
            walkthroughId={walkthroughId}
            stepNumber={step.stepNumber}
            targetBox={step.targetBox}
            highlights={highlights}
            enableZoom={false}
            alt={`Step ${step.stepNumber} screenshot`}
            className="max-h-[360px] w-full"
          />
        </div>
      )}
    </div>
  );
}

function PrintFindingItem({ finding }: { finding: PrintFinding }) {
  return (
    <div className="space-y-2 rounded-lg border border-border bg-card p-4 break-inside-avoid">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <FindingPolarityMark type={finding.type} />
          {finding.severity && (
            <SeverityDot tone={severityTone(finding.severity)} />
          )}
          <span className="text-sm font-semibold text-foreground">
            {finding.title}
          </span>
        </div>
        {typeof finding.stepNumber === "number" && (
          <span className="font-mono text-xs text-muted-foreground">
            Step {finding.stepNumber}
            {finding.targetBox ? " · target marked" : ""}
          </span>
        )}
      </div>

      {finding.uiFact && (
        <p className="text-xs text-foreground">
          <span className="font-semibold text-muted-foreground">
            Observed:{" "}
          </span>
          {finding.uiFact}
        </p>
      )}

      {finding.consequence && (
        <p className="text-xs text-muted-foreground">
          <span className="font-semibold text-foreground">Consequence: </span>
          {finding.consequence}
        </p>
      )}

      {finding.recommendation && (
        <div className="text-xs text-muted-foreground border-t border-border/50 pt-1.5 mt-1.5">
          <span className="font-semibold text-foreground">Recommendation:</span>
          <MarkdownProse
            content={finding.recommendation}
            lineBreaks
            className="text-xs prose-p:my-1"
          />
        </div>
      )}
    </div>
  );
}

export function WalkthroughPrintDocument({
  scenarioName,
  scenarioNarrative,
  persona,
  walkthroughId,
  runNumber,
  startedAt,
  completedAt,
  targetUrl,
  steps,
  findings,
  verdicts,
  whatWorked,
  openQuestions,
  className,
}: WalkthroughPrintDocumentProps) {
  const duration = formatDuration(startedAt, completedAt);
  const dateStr = formatDate(completedAt ?? startedAt);
  const problemsCount = findings.filter((f) => isProblemType(f.type)).length;

  const datelineItems = [
    ...(dateStr ? [{ label: "Date", value: dateStr }] : []),
    ...(duration ? [{ label: "Duration", value: duration }] : []),
    ...(targetUrl ? [{ label: "Target", value: targetUrl }] : []),
    { label: "Steps", value: `${steps.length}` },
    {
      label: "Findings",
      value:
        problemsCount > 0
          ? `${problemsCount} issues`
          : `${findings.length} findings`,
      tone: problemsCount > 0 ? ("critical" as const) : undefined,
    },
  ];

  return (
    <article className={cn("space-y-8 print:p-0", className)}>
      {/* Header */}
      <header className="space-y-4 border-b border-border/80 pb-6 break-inside-avoid">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {persona && <PersonaAvatar name={persona.name} size="md" />}
            <div>
              <div className="text-xs text-muted-foreground">
                {persona?.name
                  ? `As experienced by ${persona.name}`
                  : "Walkthrough Report"}
                {persona?.role ? ` · ${persona.role}` : ""}
              </div>
              <h1 className="text-xl font-semibold tracking-tight text-foreground">
                {scenarioName}
              </h1>
            </div>
          </div>
          {runNumber && (
            <span className="rounded bg-muted px-2.5 py-1 text-xs font-semibold tabular-nums text-foreground">
              Run #{runNumber}
            </span>
          )}
        </div>

        {/* Dateline */}
        <Dateline items={datelineItems} />
      </header>

      {/* Situation Narrative */}
      {scenarioNarrative && (
        <section className="space-y-2 rounded-lg border border-border bg-muted/20 p-4 break-inside-avoid">
          <Kicker as="h2">Situation</Kicker>
          <VoiceProse className="text-sm">{scenarioNarrative}</VoiceProse>
        </section>
      )}

      {/* The Human Test Verdicts */}
      {verdicts && verdicts.length > 0 && (
        <section className="space-y-3 break-inside-avoid">
          <Kicker as="h2">The Human Test</Kicker>
          <HumanTestVerdicts
            verdicts={verdicts}
            note={
              persona?.name ? `Reading ${persona.name}'s verdict` : undefined
            }
          />
        </section>
      )}

      {/* What Worked & Open Questions */}
      {((whatWorked && whatWorked.length > 0) ||
        (openQuestions && openQuestions.length > 0)) && (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 break-inside-avoid">
          {whatWorked && whatWorked.length > 0 && (
            <section className="space-y-2 rounded-lg border border-border bg-card p-4">
              <Kicker as="h3">What worked</Kicker>
              <ul className="list-disc list-inside space-y-1 text-xs text-foreground">
                {whatWorked.map((item, idx) => (
                  <li key={idx}>{item}</li>
                ))}
              </ul>
            </section>
          )}

          {openQuestions && openQuestions.length > 0 && (
            <section className="space-y-2 rounded-lg border border-border bg-card p-4">
              <Kicker as="h3">Open questions</Kicker>
              <ul className="list-disc list-inside space-y-1 text-xs text-foreground">
                {openQuestions.map((item, idx) => (
                  <li key={idx}>{item}</li>
                ))}
              </ul>
            </section>
          )}
        </div>
      )}

      {/* Findings */}
      {findings.length > 0 && (
        <section className="space-y-3">
          <div className="flex items-center justify-between border-b border-border/60 pb-2">
            <Kicker as="h2">Findings ({findings.length})</Kicker>
          </div>
          <div className="space-y-3">
            {findings.map((f) => (
              <PrintFindingItem key={f.id} finding={f} />
            ))}
          </div>
        </section>
      )}

      {/* Steps Timeline */}
      {steps.length > 0 && (
        <section className="space-y-4">
          <div className="flex items-center justify-between border-b border-border/60 pb-2">
            <Kicker as="h2">Step timeline ({steps.length} steps)</Kicker>
          </div>
          <div className="space-y-4">
            {steps.map((s) => {
              const stepFindings = findings.filter(
                (f) =>
                  f.walkthroughStepId === s.id ||
                  (typeof f.stepNumber === "number" &&
                    f.stepNumber === s.stepNumber),
              );
              const stepHighlights: ScreenshotHighlight[] = stepFindings
                .filter((f) => isValidBox(f.targetBox))
                .map((f, i) => ({
                  id: f.id,
                  box: f.targetBox!,
                  tone: f.severity ?? "destructive",
                  label: f.title,
                  index: i + 1,
                  isActive: true,
                }));
              const highlights =
                stepHighlights.length > 0
                  ? stepHighlights
                  : isValidBox(s.targetBox)
                    ? [
                        {
                          id: `step-${s.stepNumber}`,
                          box: s.targetBox,
                          tone: "destructive",
                        },
                      ]
                    : undefined;

              return (
                <PrintStepItem
                  key={s.id}
                  step={s}
                  walkthroughId={walkthroughId}
                  highlights={highlights}
                />
              );
            })}
          </div>
        </section>
      )}
    </article>
  );
}
